/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/forbid-prop-types */
import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import {
  func, object, string,
} from 'prop-types';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { Spinner } from 'ui-component-library';
import { useSelector, useDispatch } from 'react-redux';

import { trackingQueueColumnDefs, trackingLTLQueueColumnDefs } from './tracking-queue-column-defs';
import {
  TrackingQueueTableContainer,
  LastFetched,
  NumRows,
  NoRowsOverlay,
} from './tracking-queue-table.style';
import StatusIndicator from './status-indicator';
import { logger } from '../../../../utils/common';
import * as loadsApi from '../../../../utils/api/load-board';
import { PAGE_SIZE } from '../../../../env';
import { InfoSection } from '../../../containers/tracking-queue/tracking-queue.styles';
import getRowStyles from './tracking-queue-row-styles';
import { clearUpdatedLoad } from '../../../../store/updated-load/slice';
import Link from '../link/Link';
import HotKeyInfoModal from '../modals/hot-key-info-modal';
import sortShape from '../../../../shapes/sort-model';
import { MODE_TYPES } from '../../../../constants';

const ROW_HEIGHT = 36;
const HEADER_ROW_HEIGHT = 40;
// Check to see if the environment page size value is actually a number
// if not, default to 100. For test runs.
const PAGE_SIZE_VALUE = (!Number.isNaN(parseInt(PAGE_SIZE, 10))) ? parseInt(PAGE_SIZE, 10) : 100;

/**
 * The tracking queue table component
 * @param {object} params - Object containing the params for this component.
 * @param {func} params.onPeekLoad - Function to handle when a user peeks a particular load
 * @param {func} params.onUpdatedFilters - Function param to update filters options
 * @param {func} params.onSmartFilterCountsChange - Function param to update smart filters counts
 * @param {object} params.smartFilterCounts - Number of loads with a problem
 * @param {object} params.filters - Current active filters on data set
 * @param {string} params.search - Current active search on data set
 * @param {string} params.mode - Mode type
 * @return {element}
 */
const TrackingQueueTable = ({
  onPeekLoad,
  onUpdatedFilters,
  filters,
  search,
  initialSort,
  onSortChange,
  onSmartFilterCountsChange,
  smartFilterCounts,
  mode,
}) => {
  // Routing hooks
  const history = useHistory();
  const location = useLocation();

  // State hooks
  const [numRows, setNumRows] = useState(0);
  const [gridReady, setGridReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState(moment());
  const [hotkeyModalOpen, setHotkeyModalOpen] = useState(false);

  // Redux hooks
  const tableParams = useRef({});
  const updatedLoad = useSelector((state) => state.updatedLoad.load);
  const selectedLoadId = useSelector((state) => state.selectedLoad.id);
  const officeNames = useSelector((state) => state.profileSettings.data?.officeNames);
  const dispatch = useDispatch();

  // AG-Grid no content template
  // Per AG-Grid documentation: https://www.ag-grid.com/javascript-grid-overlays/
  const overlayNoRowsTemplate = `<span style="${NoRowsOverlay}">
    No loads were found with the selected filters or search query. <br> Please refine your filters or search.
  </span>`;

  // AG-Grid configuration
  const tableApi = tableParams.current?.api;
  const datasource = useRef({
    getRows: (_params) => {
      const {
        request: {
          sortModel = null, startRow,
        },
        success,
        parentNode,
      } = _params;

      const { context } = parentNode.gridOptionsWrapper.gridOptions;
      setLoading(true);
      loadsApi
        .getLoadBoard(
          startRow,
          PAGE_SIZE_VALUE,
          sortModel,
          context.filters,
          context.search,
        )
        .then((res) => {
          // Check to see if there are filter values to populate
          // our filter drop downs.
          if (res?.data?.model?.filterValues) {
            const filterOptions = res.data.model.filterValues;
            if (filterOptions) {
              onUpdatedFilters(filterOptions);
            } else {
              logger.info('No filter options returned in request');
            }
            onUpdatedFilters(res.data.model.filterValues);
          }
          const data = res?.data.model;
          if (!res.data.model.loads || res.data.model.loads.length === 0) {
            success({ rowData: [], rowCount: 0 });
            tableParams.current.api.showNoRowsOverlay();
            onSmartFilterCountsChange({
              problemLoadCount: data.problemLoadCount,
              lateLoadCount: data.lateLoadCount,
              withinTwoHoursCount: data.withinTwoHoursCount,
            });
          } else {
            tableParams.current.api.hideOverlay();
            setLastFetched(moment());
            let totalRowNumber = context.numRows;
            if (startRow === 0 && context.numRows <= PAGE_SIZE_VALUE) {
              totalRowNumber = data.loads.length;
              setNumRows(totalRowNumber);
            } else if (startRow >= context.numRows) {
              totalRowNumber = context.numRows + data.loads.length;
              setNumRows(totalRowNumber);
            }
            const lastRow = (totalRowNumber % PAGE_SIZE_VALUE) > 0 ? totalRowNumber : -1;
            onSmartFilterCountsChange({
              problemLoadCount: data.problemLoadCount,
              lateLoadCount: data.lateLoadCount,
              withinTwoHoursCount: data.withinTwoHoursCount,
            });
            success({ rowData: data.loads, rowCount: lastRow });
            // If there's a selected row in state but the table doesn't know, reselect it
            if (tableParams.current.api.getSelectedRows()?.length === 0 && (selectedLoadId || selectedLoadId === 0)) {
              const rowNode = tableParams.current.api.getRowNode(selectedLoadId);
              if (rowNode) {
                rowNode.setSelected(true);
                tableParams.current.api.ensureIndexVisible(rowNode.rowIndex, 'top');
              }
            }
          }
          setLoading(false);
        })
        .catch((error) => {
          // Log error and dismiss loading overlay
          logger.error(error);
          setLoading(false);
        });
    },
  });

  /**
   * Pushes user to the details view
   * @param {string} loadId - ID of the load
   * @return {void}
   */
  const pushToDetailsView = (loadId, salesOrderBk) => {
    history.push({ pathname: `/loads/detail/${loadId}`, search: `?modeType=${mode}`, state: { prevPath: location.pathname, salesOrderBk } });
  };

  /**
   * Configures data source and sets resize listener
   * for table.
   * @param {object} params - API Params from AG-Grid
   * @return {void}
   */
  const onGridReady = (params) => {
    // Remember table params so we can use it for
    tableParams.current = params;
    setGridReady(true);
    // Set initial sort if it's defined in URL query string
    params.columnApi.applyColumnState(initialSort);
  };

  /**
   * In the event of a row update, set the updated row's
   * data to the new data in the table cache.
   */
  useEffect(() => {
    /**
     * Updates loads with problems count. Used in a row
     * re-render.
     * @param {object} previousLoadData - Load data
     * @param {*} newLoadData - New Load data
     * @return {void}
     */
    const updateProblemSmartFilterCount = (previousLoadData, newLoadData) => {
      const previousProblems = previousLoadData.problems;
      const newProblems = newLoadData.problems;
      if (!newProblems) {
        return;
      }
      if (newProblems.length > previousProblems.length) {
        onSmartFilterCountsChange({
          problemLoadCount: smartFilterCounts.problemLoadCount + (newProblems.length - previousProblems.length),
        }, newProblems);
      } else if (newProblems.length < previousProblems.length) {
        onSmartFilterCountsChange({
          problemLoadCount: smartFilterCounts.problemLoadCount - (previousProblems.length - newProblems.length),
        }, newProblems);
      }
    };

    if (gridReady && tableApi && updatedLoad?.loadId) {
      const rowNode = tableApi.getRowNode(updatedLoad.loadId);
      // check to see if we need to update smart filter counts
      if (rowNode) {
        updateProblemSmartFilterCount(rowNode.data, updatedLoad);
        rowNode.setData({ ...rowNode.data, ...updatedLoad });
        tableApi.refreshCells({ rowNodes: [rowNode], force: true });
        dispatch(clearUpdatedLoad());
      }
    }
  }, [updatedLoad, gridReady, tableApi, dispatch, onSmartFilterCountsChange, smartFilterCounts]);

  /**
   * Use-effect hook to reset the table rows number after a change of filters or search
   */
  useEffect(() => {
    setNumRows(0);
    if (gridReady && tableParams?.current?.api) {
      tableParams.current.api.setServerSideDatasource(datasource.current);
    }
  }, [gridReady, filters, search, dispatch, officeNames]);

  /**
   * Updates infinite scrolling cache every five minutes.
   * Unset the interval on clean up.
   */
  useEffect(() => {
    let updateInterval = null;
    if (gridReady) {
      /**
       * Callback function for refreshing the
       * current infinite scroll cache in the
       * loads table. Called on interval that is
       * set above in useEffect
       * @return {void}
       */
      const handleGridRefresh = () => {
        if (tableParams?.current?.api) {
          tableParams.current.api.refreshInfiniteCache();
        }
      };

      updateInterval = setInterval(handleGridRefresh, 300000);
    }
    return () => {
      clearInterval(updateInterval);
    };
  }, [gridReady]);

  const checkResize = () => {
    const trackingQueueTableContainer = document.getElementById('tracking-queue-container');
    // Check to see if trackingQueueTableContainer is not defined.
    // If it isn't defined, there is a hanging event listener.
    // 1860 px is 1920 - 30px padding on either side
    if (tableApi && trackingQueueTableContainer && trackingQueueTableContainer.offsetWidth >= 1860) {
      tableApi.sizeColumnsToFit();
    }
  };

  /**
  * Handles when a user clicks a cell. Opens the peak view
  * on the load that was clicked.
  * @param {object} event - Cell clicked event
  * @return {void}
  */
  const handleCellClicked = (event) => {
    if (event.data?.loadId) {
      onPeekLoad(event.data.loadId, event.data.billOfLadingNumber);
    }
  };

  /**
  * Handles when a row is double clicked
  * by a user.
  * @param {object} event - Event object
  */
  const handleRowDoubleClicked = (event) => {
    pushToDetailsView(event.data.loadId, event.data.billOfLadingNumber);
  };

  /**
   * Handles when a user clicks enter in a cell
   * @param {object} event - Event object
   * @return {void}
   */
  const handleRowClickedEnter = (event) => {
    const selectedRows = tableApi.getSelectedRows();
    if (event.event.code === 'Enter' && selectedRows[0]?.loadId) {
      pushToDetailsView(selectedRows[0].loadId, selectedRows[0].billOfLadingNumber);
    }
  };

  /**
   * Handles navigating from row to row using the navigation keys
   * @param {object} params - Table Params
   * @return {object} - New navigation position
   */
  const navigateToNextCell = (params) => {
    const KEY_UP = 38;
    const KEY_DOWN = 40;
    const previousCell = params.previousCellPosition;
    const suggestedNextCell = params.nextCellPosition;
    let nextRowIndex;
    let renderedRowCount;
    switch (params.key) {
      case KEY_UP:
        nextRowIndex = previousCell.rowIndex - 1;
        if (nextRowIndex < -1) {
          return null;
        }
        tableParams.current.api.forEachNode((node) => {
          if (previousCell.rowIndex - 1 === node.rowIndex) {
            node.setSelected(true);
            onPeekLoad(node.data?.loadId, node.data?.billOfLadingNumber);
          }
        });
        return {
          rowIndex: nextRowIndex,
          column: previousCell.column,
          floating: previousCell.floating,
        };
      case KEY_DOWN:
        nextRowIndex = previousCell.rowIndex + 1;
        renderedRowCount = tableParams.current.api.getModel().getRowCount();
        tableParams.current.api.forEachNode((node) => {
          if (previousCell.rowIndex + 1 === node.rowIndex) {
            node.setSelected(true);
            onPeekLoad(node.data?.loadId, node.data?.billOfLadingNumber);
          }
        });
        if (nextRowIndex >= renderedRowCount) {
          return null;
        }
        return {
          rowIndex: nextRowIndex,
          column: previousCell.column,
          floating: previousCell.floating,
        };
      default:
        return suggestedNextCell;
    }
  };

  /**
   * Opens the hotkey modal
   * @return {void}
   */
  const openHotKeyModal = () => {
    setHotkeyModalOpen(true);
  };

  /**
   * Closes the hotkey modal
   * @return {void}
   */
  const closeHotKeyModal = () => {
    setHotkeyModalOpen(false);
  };

  /**
   * Specifies an ID for the AG-Grid table
   * @param {object} node - Row node
   * @return {string}
   */
  const getRowNodeId = (node) => node.loadId;

  return (
    <TrackingQueueTableContainer className="ag-theme-alpine" data-testid="tracking-queue-container" id="tracking-queue-container">
      <HotKeyInfoModal
        open={hotkeyModalOpen}
        onClose={closeHotKeyModal}
      />
      {loading && <Spinner overlay zIndex={100} />}
      <InfoSection>
        <Link onClick={openHotKeyModal} text="Learn about Tracking Queue keyboard shortcuts" />
        <InfoSection>
          <LastFetched>
            Data Last Updated:
            <strong>{moment(lastFetched).format('HH:mm:ss')}</strong>
          </LastFetched>
          <NumRows>
            # of Rows Displayed:
            <strong>{numRows}</strong>
          </NumRows>
        </InfoSection>
      </InfoSection>

      <AgGridReact
        columnDefs={(mode === MODE_TYPES.truckload) ? trackingQueueColumnDefs : trackingLTLQueueColumnDefs}
        onGridReady={onGridReady}
        onCellClicked={handleCellClicked}
        onSortChanged={onSortChange}
        frameworkComponents={{ statusIndicator: StatusIndicator }}
        cacheBlockSize={PAGE_SIZE_VALUE}
        maxBlocksInCache={1}
        rowModelType="serverSide"
        serverSideStoreType="partial"
        embedFullWidthRows
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_ROW_HEIGHT}
        onRowDoubleClicked={handleRowDoubleClicked}
        getRowStyle={getRowStyles}
        defaultColDef={{ sortable: true }}
        datasource={datasource.current}
        context={{
          filters,
          search,
          numRows,
          officeNames,
        }}
        rowSelection="single"
        navigateToNextCell={navigateToNextCell}
        getRowNodeId={getRowNodeId}
        overlayNoRowsTemplate={overlayNoRowsTemplate}
        onCellKeyDown={handleRowClickedEnter}
        onGridSizeChanged={checkResize}
        refresh
      />
    </TrackingQueueTableContainer>
  );
};

TrackingQueueTable.propTypes = {
  onSmartFilterCountsChange: func.isRequired,
  onPeekLoad: func,
  search: string,
  onUpdatedFilters: func,
  filters: object,
  smartFilterCounts: object,
  initialSort: sortShape,
  onSortChange: func,
  mode: string.isRequired,
};

TrackingQueueTable.defaultProps = {
  onPeekLoad: () => { },
  onUpdatedFilters: () => { },
  search: null,
  filters: {},
  smartFilterCounts: {},
  initialSort: {},
  onSortChange: () => { },
};

export default TrackingQueueTable;
